import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Share2, Copy, Check, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { gbp } from './PackageResults';

const t = (iso) => (iso ? (iso.includes('T') ? iso.slice(11, 16) : iso.slice(0, 5)) : '');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const d = (iso) => { if (!iso) return ''; const x = new Date(`${iso.slice(0, 10)}T00:00:00`); return Number.isNaN(x.getTime()) ? iso : `${DAYS[x.getDay()]} ${x.getDate()} ${MONTHS[x.getMonth()]} ${x.getFullYear()}`; };
const plural = (n, w, p = `${w}s`) => `${n} ${n === 1 ? w : p}`;
const stopsText = (leg) => (leg.stops === 0 ? 'Direct' : `${plural(leg.stops, 'stop')}${leg.layovers?.length ? ` · layover ${leg.layovers.map((l) => `${l.duration}${l.airport ? ` in ${l.airport}` : ''}`).join(', ')}` : ''}`);
const legLines = (label, leg) => [
  `*${label}* — ${d(leg.departure_date)}`,
  `${leg.from} ${t(leg.departure_time)} ➝ ${leg.to} ${t(leg.arrival_time)}${leg.duration ? ` (${leg.duration})` : ''} · ${stopsText(leg)}`,
];
const hotelLines = (city, h) => [
  `🏨 *${city} — ${h.name}* ${'⭐'.repeat(h.stars)}`,
  `📅 ${d(h.checkin)} ➝ ${d(h.checkout)} (${plural(h.nights, 'night')})`,
  `🛏️ ${h.room_type} · 🍽️ ${h.meal_plan_name}${h.distance_label ? `\n📍 ${h.distance_label}` : ''}`,
];

export const packageLink = (pkg) => `${window.location.origin}/?view=umrah&package=${pkg.id}`;

export const packageText = (pkg) => {
  const p = pkg.passengers;
  const paxParts = [plural(p.adults, 'adult'), p.children ? plural(p.children, 'child', 'children') : null, p.infants ? plural(p.infants, 'infant') : null].filter(Boolean);
  const order = pkg.itinerary?.order || ['makkah', 'madinah'];
  const hotels = order.flatMap((c, i) => [...hotelLines(c === 'makkah' ? 'Makkah' : 'Madinah', pkg[c]), ...(i < order.length - 1 ? [''] : [])]);
  const ziyarat = (pkg.ziyarat?.options || []).filter((o) => pkg.ziyarat.selected.includes(o.code));
  const lines = [
    `🕋 *FLIGHT380 UMRAH PACKAGE — ${String(pkg.category || '').toUpperCase()}*`,
    `👥 ${plural(p.total, 'traveller')} (${paxParts.join(', ')}) · ${pkg.room_summary || pkg.room_type}`,
    `🗓️ ${d(pkg.flight.outbound.departure_date)} ➝ ${d(pkg.flight.inbound.departure_date)}`,
    '',
    `✈️ *FLIGHTS — ${pkg.flight.airline}${pkg.flight.open_jaw ? ' (open-jaw)' : ''}*`,
    ...legLines('Outbound', pkg.flight.outbound),
    ...legLines('Return', pkg.flight.inbound),
    '',
    '🏨 *HOTELS*',
    ...hotels,
    '',
    '🚐 *TRANSFERS*',
    `${pkg.transport.name} · ${pkg.transport.journey}`,
    '',
    '🛂 *VISA*',
    pkg.visa.summary || pkg.visa.name,
    ...(ziyarat.length ? ['', '🕌 *ZIYARAT*', ...ziyarat.map((z) => `${z.name} — ${z.duration} (${z.vehicle_name})`)] : []),
    '',
    '✅ *PACKAGE INCLUDES*',
    ...pkg.includes.map((x) => `• ${x}`),
    '',
    '💷 *PRICE*',
    `*Per person: ${gbp(pkg.price.per_person)}*`,
    `*Total for ${plural(p.total, 'traveller')}: ${gbp(pkg.price.total)}*`,
    'All taxes & fees included. No hidden charges.',
    ...(pkg.deposit?.available ? ['', `💳 *Reserve today with a ${pkg.deposit.percent}% deposit of ${gbp(pkg.deposit.deposit)}*`, `Balance of ${gbp(pkg.deposit.balance)} due by ${d(pkg.deposit.balance_due_date)}`] : []),
    '',
    `🔗 *View & book online:* ${packageLink(pkg)}`,
    '',
    '📞 *Flight380 Umrah Team*',
    'Call / WhatsApp: 01582 800 304',
    '🛡️ ATOL Protected · Secure online payment',
    '',
    '_Terms & Conditions: Prices are live and subject to availability at the time of booking. Flights are non-refundable once ticketed. Hotel check-in from 15:00, check-out by 12:00. Visa processing is subject to Saudi authority approval; all travellers must hold a passport valid for at least 6 months from the date of travel. Deposit bookings require the balance to be paid by the due date shown or the booking may be cancelled. Full terms at flight380.co.uk/terms._',
  ];
  return lines.join('\n');
};

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch (e) {
    const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); return true; } catch { return false; } finally { document.body.removeChild(ta); }
  }
};

export const SharePackage = ({ pkg, compact = false }) => {
  const [done, setDone] = useState(null);
  const flash = (k, msg) => { setDone(k); toast.success(msg); setTimeout(() => setDone(null), 1800); };
  const copyFull = async () => { if (await copy(packageText(pkg))) flash('full', 'Full package copied — paste it into WhatsApp, email or a quote'); else toast.error('Could not copy'); };
  const share = async () => {
    const url = packageLink(pkg);
    if (navigator.share) { try { await navigator.share({ title: `Flight380 ${pkg.category}`, text: packageText(pkg), url }); return; } catch (e) { if (e?.name === 'AbortError') return; } }
    if (await copy(url)) flash('link', 'Package link copied');
  };
  const wa = `https://wa.me/?text=${encodeURIComponent(packageText(pkg))}`;
  const size = compact ? 'sm' : 'default';
  return (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? '' : 'pt-1'}`} data-testid={`pkg-share-${pkg.id}`}>
      <Button type="button" variant="outline" size={size} onClick={copyFull} className="border-slate-300" data-testid={`pkg-copy-full-${pkg.id}`}>{done === 'full' ? <Check className="h-4 w-4 mr-1 text-emerald-600" /> : <Copy className="h-4 w-4 mr-1" />}{done === 'full' ? 'Copied' : 'Copy full package'}</Button>
      <Button type="button" variant="outline" size={size} onClick={share} className="border-slate-300" data-testid={`pkg-share-link-${pkg.id}`}>{done === 'link' ? <Check className="h-4 w-4 mr-1 text-emerald-600" /> : <Share2 className="h-4 w-4 mr-1" />}{done === 'link' ? 'Link copied' : 'Share link'}</Button>
      <a href={wa} target="_blank" rel="noreferrer" className={`inline-flex items-center rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold hover:bg-emerald-100 ${compact ? 'h-9 px-3 text-sm' : 'h-10 px-4 text-sm'}`} data-testid={`pkg-share-whatsapp-${pkg.id}`}><MessageCircle className="h-4 w-4 mr-1" />WhatsApp</a>
    </div>
  );
};
